/**
 * Money conversion helpers for integer USD cents and micro-USD.
 *
 * The historical compatibility unit is integer cents. The precise billing unit
 * is now micro-USD: 1 USD = 1,000,000 micros and 1 cent = 10,000 micros.
 *
 * Decimal-string output (not decimal-number) is deliberate: a JSON number
 * `5.00` loses its trailing zero on parse and round-trips as `5`, which
 * breaks downstream currency formatting. Strings preserve the exact shape.
 *
 * Kept as pure functions — no Intl, no locale — so Workers runtimes,
 * Node tests, and Vite-bundled consoles all produce the same output.
 */

/**
 * Convert an integer cents value to a decimal-string with exactly two
 * fractional digits. Negative values are prefixed with `-`. `NaN` /
 * non-finite inputs throw — callers must sanitize upstream (this function
 * should never see bogus data from a validated SQL INTEGER column).
 *
 * Examples:
 *   centsToUsdString(500)   → "5.00"
 *   centsToUsdString(-125)  → "-1.25"
 *   centsToUsdString(0)     → "0.00"
 *   centsToUsdString(5)     → "0.05"
 */
export function centsToUsdString(cents: number): string {
  if (!Number.isFinite(cents)) {
    throw new Error(`centsToUsdString: non-finite input ${cents}`);
  }
  const rounded = Math.trunc(cents);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const dollars = Math.floor(abs / 100);
  const fractional = abs % 100;
  const paddedFrac = fractional < 10 ? `0${fractional}` : `${fractional}`;
  return `${sign}${dollars}.${paddedFrac}`;
}

export const USD_MICROS_PER_USD = 1_000_000;
export const USD_MICROS_PER_CENT = 10_000;

/**
 * Convert micro-USD to the compatibility integer-cents view.
 *
 * This deliberately floors absolute value, rather than rounds. Cents fields are
 * retained only for old readers; rounding them would reintroduce sub-cent
 * inflation. Negative ledger entries mirror the positive floor so a 0.001 USD
 * debit remains 0 compatibility cents instead of becoming -1 cent.
 */
export function usdMicrosToCentsFloor(micros: number): number {
  if (!Number.isFinite(micros)) {
    throw new Error(`usdMicrosToCentsFloor: non-finite input ${micros}`);
  }
  const truncated = Math.trunc(micros);
  const cents = Math.floor(Math.abs(truncated) / USD_MICROS_PER_CENT);
  if (cents === 0) return 0;
  return truncated < 0 ? -cents : cents;
}

export function centsToUsdMicros(cents: number): number {
  if (!Number.isFinite(cents)) {
    throw new Error(`centsToUsdMicros: non-finite input ${cents}`);
  }
  return Math.trunc(cents) * USD_MICROS_PER_CENT;
}

/**
 * Convert integer CNY cents to micro-USD at a CNY-per-USD policy rate.
 *
 * Same formula receipts use when crediting a CNY payment:
 * `round(cents × 10_000 / rate)`. Credit grants lock this conversion at
 * approval so a later policy-rate change cannot move an already-granted
 * USD ceiling (router#2036).
 */
export function cnyCentsToUsdMicros(cnyCents: number, cnyPerUsd: number): number {
  if (!Number.isSafeInteger(cnyCents)) {
    throw new Error(`cnyCentsToUsdMicros: non-integer cents ${cnyCents}`);
  }
  if (!Number.isFinite(cnyPerUsd) || cnyPerUsd <= 0) {
    throw new Error(`cnyCentsToUsdMicros: invalid CNY-per-USD rate ${cnyPerUsd}`);
  }
  const micros = Math.round((cnyCents * 10_000) / cnyPerUsd);
  if (!Number.isSafeInteger(micros)) {
    throw new Error(`cnyCentsToUsdMicros: result is not a safe integer (${micros})`);
  }
  return micros;
}

/**
 * Read a dual-written micro-USD/cents pair using the migration-compatible
 * policy shared across platform and ops surfaces.
 *
 * The precise `*_usd_micros` column is authoritative once populated. During
 * the cents-to-micros cutover, some old rows can still expose `0` micros while
 * the compatibility cents column has the real historical amount, so zero
 * micros falls back to cents. A true zero stays zero because its cents value is
 * also zero or absent.
 */
export function microsFromDual(
  micros: number | null | undefined,
  cents: number | null | undefined,
): number {
  if (typeof micros === 'number') {
    if (!Number.isFinite(micros)) {
      throw new Error(`microsFromDual: non-finite micros input ${micros}`);
    }
    if (micros !== 0) {
      return Math.trunc(micros);
    }
  }
  return centsToUsdMicros(cents ?? 0);
}

/**
 * Effective spendable balance in micro-USD: raw account balance plus the
 * account's own credit headroom (epic #1033 E4-C3, #1127).
 *
 * `creditLimitUsdMicros` is the RAW per-account `accounts.credit_limit_usd_micros`
 * column — NULL/undefined means "no credit line" and coalesces to 0. It is
 * deliberately NOT the `credit_settings` default: that platform default is the
 * single-sign application ceiling, never an implicit grant, so an account
 * without an explicit line has zero overdraft headroom. Passing the inherited
 * effective limit here would silently give every account the default overdraft.
 *
 * This is the one definition the charge gate, the low-balance classifiers, and
 * the balance wire contract all share so "how much can this account still
 * spend" cannot drift between layers.
 */
export function effectiveBalanceUsdMicros(
  balanceUsdMicros: number,
  creditLimitUsdMicros: number | null | undefined,
): number {
  if (!Number.isFinite(balanceUsdMicros)) {
    throw new Error(`effectiveBalanceUsdMicros: non-finite balance ${balanceUsdMicros}`);
  }
  const credit = typeof creditLimitUsdMicros === 'number' && Number.isFinite(creditLimitUsdMicros)
    ? Math.trunc(creditLimitUsdMicros)
    : 0;
  return Math.trunc(balanceUsdMicros) + credit;
}

/**
 * Normalize a raw per-account credit limit to non-negative micro-USD.
 * NULL/undefined/negative → 0 (no line). Mirrors the SQL
 * `COALESCE(credit_limit_usd_micros, 0)` the charge gate uses.
 */
export function availableCreditUsdMicros(creditLimitUsdMicros: number | null | undefined): number {
  if (typeof creditLimitUsdMicros !== 'number' || !Number.isFinite(creditLimitUsdMicros)) {
    return 0;
  }
  const truncated = Math.trunc(creditLimitUsdMicros);
  return truncated > 0 ? truncated : 0;
}

/**
 * Convert an integer micro-USD value to a decimal-string with up to six
 * fractional digits, trimming trailing zeros while keeping at least two
 * fractional digits for currency-shaped compatibility output.
 */
export function microsToUsdString(micros: number): string {
  if (!Number.isFinite(micros)) {
    throw new Error(`microsToUsdString: non-finite input ${micros}`);
  }
  const rounded = Math.trunc(micros);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const dollars = Math.floor(abs / USD_MICROS_PER_USD);
  const fractional = String(abs % USD_MICROS_PER_USD).padStart(6, '0');
  const trimmed = fractional.replace(/0+$/, '');
  const displayFraction = trimmed.length === 0 ? '00' : trimmed.padEnd(2, '0');
  return `${sign}${dollars}.${displayFraction}`;
}

/**
 * Parse a decimal-string or decimal-number into integer cents. Accepts
 * "5", "5.0", "5.00", "-1.25", 5, 5.5. Returns `null` on anything that
 * can't be interpreted cleanly (empty string, NaN, Infinity, non-numeric
 * text). Caller decides how to surface the error.
 *
 * Rounds half-away-from-zero to mirror most human expectations — matches
 * the half-away-from-zero convention used by the retired 0008 backfill.
 */
export function parseUsdToCents(raw: unknown): number | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    return roundHalfAway(raw * 100);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return roundHalfAway(parsed * 100);
  }
  return null;
}

export function parseUsdToMicros(raw: unknown): number | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    return roundHalfAway(raw * USD_MICROS_PER_USD);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return roundHalfAway(parsed * USD_MICROS_PER_USD);
  }
  return null;
}

function roundHalfAway(n: number): number {
  if (n >= 0) return Math.floor(n + 0.5);
  return -Math.floor(-n + 0.5);
}
